import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  MpgCreateRequest,
  MpgUpdateRequest,
  MpgListQuery,
  MpgListResponse,
  MpgResponse,
} from '@produzionejb/types';
import { api } from './api-client';

type MpgCloseResponse = MpgResponse & {
  autoRolloverNextId: string | null;
  autoRolloverNextCode: string | null;
};

const QK = {
  list: (q: Partial<MpgListQuery>) => ['mpg-documents', 'list', q] as const,
  one: (id: string) => ['mpg-documents', 'one', id] as const,
};

function toQueryString(q: Partial<MpgListQuery>): string {
  const params = new URLSearchParams();
  if (q.page) params.set('page', String(q.page));
  if (q.pageSize) params.set('pageSize', String(q.pageSize));
  if (q.status) params.set('status', q.status);
  if (q.periodId) params.set('periodId', q.periodId);
  if (q.workDateFrom) params.set('workDateFrom', new Date(q.workDateFrom).toISOString());
  if (q.workDateTo) params.set('workDateTo', new Date(q.workDateTo).toISOString());
  const s = params.toString();
  return s ? `?${s}` : '';
}

export function useMpgList(query: Partial<MpgListQuery>) {
  return useQuery({
    queryKey: QK.list(query),
    queryFn: () => api<MpgListResponse>(`/api/documents/mpg${toQueryString(query)}`),
    staleTime: 10_000,
  });
}

export function useMpg(id: string | undefined) {
  return useQuery({
    queryKey: id ? QK.one(id) : ['mpg-documents', 'one', 'NIL'],
    queryFn: () => api<MpgResponse>(`/api/documents/mpg/${id}`),
    enabled: !!id,
  });
}

export function useCreateMpg() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: MpgCreateRequest) =>
      api<MpgResponse>('/api/documents/mpg', { method: 'POST', body: data }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['mpg-documents', 'list'] });
    },
  });
}

export function useUpdateMpg(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: MpgUpdateRequest) =>
      api<MpgResponse>(`/api/documents/mpg/${id}`, { method: 'PATCH', body: data }),
    onSuccess: (updated) => {
      qc.setQueryData(QK.one(id), updated);
      void qc.invalidateQueries({ queryKey: ['mpg-documents', 'list'] });
    },
  });
}

export function useCloseMpg(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<MpgCloseResponse>(`/api/documents/mpg/${id}/close`, { method: 'POST', body: {} }),
    onSuccess: (updated) => {
      qc.setQueryData(QK.one(id), updated);
      void qc.invalidateQueries({ queryKey: ['mpg-documents', 'list'] });
    },
  });
}

export function useDeleteMpg() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`/api/documents/mpg/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['mpg-documents', 'list'] });
    },
  });
}
